import { getSelectorElements } from '@/api/component-service-api'
import {
  type V1SelectorElement,
  type V1SelectorComponent
} from 'mnvm-mypage-app-ts-if'
import type React from 'react'
import {
  type FormElementsSelectorAction,
  FETCHING_DATA_SELECTOR_ELEMENTS,
  ADD_SELECTOR_ELEMENTS,
  RESET,
  ON_RESET_SELECT,
  type FormElementsSelectorState,
  ON_SELECT_ALL,
  ON_SELECT,
  ON_BACK,
  ADD_DEFAULT_VALUES_SELECTOR_ELEMENTS,
  ADD_SCROLL_POSITION,
  ON_REMOVE_LAST_SCROLL_POSITION
} from './reducer'
import _ from 'lodash'
import { type TJobPostingAdvancedSearchConditionBackup } from '@/hooks/useJobPostingAdvancedSearchConditionBackup'
import { type ScrollToIndexOptions } from 'react-viewport-list'

const fetchData = async ({
  selectorComponentId,
  targetId,
  depth
}: {
  selectorComponentId: string
  targetId?: string
  depth?: number
}): Promise<V1SelectorComponent | undefined> => {
  const view = await getSelectorElements({
    selectorComponentId,
    body: {
      targetId,
      depth
    }
  })
  return view.component
}

export const initMain = (
  selectorComponentId: string,
  dispatch: React.Dispatch<FormElementsSelectorAction<any>>
): void => {
  dispatch({ type: FETCHING_DATA_SELECTOR_ELEMENTS })
  fetchData({ selectorComponentId })
    .then((value) => {
      dispatch({ type: ADD_SELECTOR_ELEMENTS, payload: value })
    })
    .finally(() => {
      dispatch({ type: FETCHING_DATA_SELECTOR_ELEMENTS })
    })
}

export const onReset = (
  dispatch: React.Dispatch<FormElementsSelectorAction<any>>
): void => {
  dispatch({ type: RESET })
}

export const onResetSelected = (
  dispatch: React.Dispatch<FormElementsSelectorAction<any>>
): void => {
  dispatch({ type: ON_RESET_SELECT })
}

export const onLoadNextDepth = (
  target: V1SelectorElement,
  state: FormElementsSelectorState,
  dispatch: React.Dispatch<FormElementsSelectorAction<any>>
): void => {
  const { items } = state
  const component = _.last(items)
  if (component?.selectorComponentId === undefined) return

  const { selectorComponentId, depth, maxDepth } = component

  if (depth === undefined || maxDepth === undefined) return

  if (depth + 1 > maxDepth) return

  dispatch({ type: FETCHING_DATA_SELECTOR_ELEMENTS, payload: target })
  fetchData({ selectorComponentId, targetId: target.id, depth: depth + 1 })
    .then((value) => {
      dispatch({ type: ADD_SELECTOR_ELEMENTS, payload: value })
    })
    .finally(() => {
      dispatch({ type: FETCHING_DATA_SELECTOR_ELEMENTS })
    })
}

export interface OnSelectPayload {
  itemSelected: V1SelectorElement
  backUpData: TJobPostingAdvancedSearchConditionBackup | undefined
}
export const onSelect = (
  target: OnSelectPayload,
  dispatch: React.Dispatch<FormElementsSelectorAction<any>>
): void => {
  dispatch({ type: ON_SELECT, payload: target })
}

export interface OnSelectAllPayload {
  checked: boolean
  backUpData: TJobPostingAdvancedSearchConditionBackup | undefined
}
export const onSelectAll = (
  target: OnSelectAllPayload,
  dispatch: React.Dispatch<FormElementsSelectorAction<any>>
): void => {
  dispatch({ type: ON_SELECT_ALL, payload: target })
}

export const onBack = (
  dispatch: React.Dispatch<FormElementsSelectorAction<any>>
): void => {
  dispatch({ type: ON_BACK })
}

export interface DefaultValuePayload {
  values: string[]
  municipalityMap: Record<string, string[]>
}
export const addDefaultValues = (
  target: DefaultValuePayload,
  dispatch: React.Dispatch<FormElementsSelectorAction<any>>
): void => {
  dispatch({ type: ADD_DEFAULT_VALUES_SELECTOR_ELEMENTS, payload: target })
}

export const addScrollPosition = (
  target: ScrollToIndexOptions,
  dispatch: React.Dispatch<FormElementsSelectorAction<any>>
): void => {
  dispatch({ type: ADD_SCROLL_POSITION, payload: target })
}

export const onRemoveLastScrollPosition = (
  dispatch: React.Dispatch<FormElementsSelectorAction<any>>
): void => {
  dispatch({ type: ON_REMOVE_LAST_SCROLL_POSITION })
}

export const getSelectedLabels = (
  state: FormElementsSelectorState
): string[] => {
  return state.values.reduce<string[]>((previous, value) => {
    const label = state.allElements.find((e) => {
      return e.data === value
    })?.label
    if (label !== undefined) return [...previous, label]
    return previous
  }, [])
}
